import { HowToAnswer } from "../components/interactions/HowToAnswer";
/**
 * WCC360 v2 — "Your career matches" chapter screens.
 *
 * `ChildhoodDreamScreen` (X-DREAM-001) and `CareerPullScreen` (X-PULL-001).
 * Both are stated interest, both are scored, both are bounded — see
 * `careerPull.ts` for exactly how small the effect is.
 */
import { useMemo, useState } from "react";
import { ArrowRight, Check, X } from "lucide-react";
import { ChoiceScreen } from "../components/ChoiceScreen";
import {
  CHILDHOOD_DREAMS, CAREER_PULLS, MAX_PULLS, NO_DREAM_ID, pullById,
} from "../careerPull";
import { useAssessmentV2 } from "../store";
import { cn } from "@/lib/utils";

interface Props { onNext: () => void }

const DREAM_COPY: Record<string, { title: string; subtitle: string }> = {
  "12to14": {
    title: "When you were little, what did you want to be?",
    subtitle:
      "Think back to when you were about six or seven. There's no wrong answer, and it doesn't tie you to anything.",
  },
  "15to18": {
    title: "When you were about 10, what did you want to be?",
    subtitle:
      "Not what you want now — what you told people back then. It's often a clue worth keeping.",
  },
  "18plus": {
    title: "When you were about 10, what did you want to be?",
    subtitle:
      "Not what you do now — what you'd have said as a child. It usually points at something that still matters to you.",
  },
};

export function ChildhoodDreamScreen({ onNext }: Props) {
  const { state, setPart1, band } = useAssessmentV2();
  const copy = DREAM_COPY[band] ?? DREAM_COPY["18plus"];
  const [value, setValue] = useState<string | undefined>(state.part1.childhood_dream);

  return (
    <ChoiceScreen
      title={copy.title}
      subtitle={copy.subtitle}
      choices={CHILDHOOD_DREAMS.map((d) => ({
        value: d.id, label: d.label, hint: d.hint,
      }))}
      value={value}
      onSelect={setValue}
      onContinue={() => {
        if (!value) return;
        setPart1({ childhood_dream: value });
        onNext();
      }}
    />
  );
}

const PULL_COPY: Record<string, { title: string; subtitle: string }> = {
  "12to14": {
    title: "Which of these worlds would you want to look inside?",
    subtitle:
      "Pick up to three. This isn't you choosing a job — it's you telling us what to show you first.",
  },
  "15to18": {
    title: "Which kinds of work would you want to see matches from?",
    subtitle:
      "Pick up to three. Your answers so far decide the matches; this decides what we make sure you get to see.",
  },
  "18plus": {
    title: "Which kinds of work would you want to see matches from?",
    subtitle:
      "Pick up to three. Everything you've told us still drives the ranking — this makes sure the work you're curious about gets in front of you.",
  },
};

export function CareerPullScreen({ onNext }: Props) {
  const { state, setPart1, band } = useAssessmentV2();
  const copy = PULL_COPY[band] ?? PULL_COPY["18plus"];
  const [selected, setSelected] = useState<string[]>(state.part1.career_pull_ids ?? []);

  const full = selected.length >= MAX_PULLS;

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_PULLS
          ? prev
          : [...prev, id],
    );

  const chips = useMemo(
    () => selected.map((id) => pullById(id)).filter(Boolean),
    [selected],
  );

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col">
      <h2 className="font-display text-2xl md:text-3xl font-medium leading-snug">
        {copy.title}
      </h2>
      <p className="mt-3 text-muted-foreground leading-relaxed">{copy.subtitle}</p>
      <div className="mt-4"><HowToAnswer>Pick up to {MAX_PULLS}. Tap a pick again to remove it.</HowToAnswer></div>

      <p className="mt-4 text-xs uppercase tracking-[0.16em] text-primary" aria-live="polite">
        {selected.length === 0
          ? "Pick at least one to continue"
          : `${selected.length} of ${MAX_PULLS} chosen`}
      </p>

      {chips.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {chips.map((o) => (
            <li key={o!.id}>
              <button
                type="button"
                onClick={() => toggle(o!.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/40
                  bg-primary/10 px-3 py-1.5 text-sm text-foreground min-h-[44px]
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {o!.label}
                <X className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">Remove {o!.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <ul className="mt-6 grid gap-3 sm:grid-cols-2" role="group" aria-label={copy.title}>
        {CAREER_PULLS.map((o) => {
          const on = selected.includes(o.id);
          const disabled = !on && full;
          return (
            <li key={o.id}>
              <button
                type="button"
                aria-pressed={on}
                disabled={disabled}
                onClick={() => toggle(o.id)}
                className={cn(
                  "w-full min-h-[64px] rounded-3xl border px-5 py-4 text-left text-sm leading-snug",
                  "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                  on
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-card text-foreground/90 hover:border-primary/40",
                  disabled && "opacity-45",
                )}
              >
                <span className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border",
                      on ? "border-primary bg-primary text-primary-foreground" : "border-border",
                    )}
                  >
                    {on && <Check className="h-3 w-3" />}
                  </span>
                  {o.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        disabled={selected.length === 0}
        onClick={() => {
          setPart1({ career_pull_ids: selected });
          onNext();
        }}
        className="mt-8 inline-flex min-h-[48px] items-center justify-center gap-2 self-start
          rounded-full bg-primary px-7 text-sm font-semibold text-primary-foreground
          disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        Continue
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
